import { actionCreators } from '../../store/actions';
import ReduxActions from '../../store/ReduxActions';
import { ErrorHandlerInterface, IMap, Order, OrderData } from '../exports/Interfaces';
import { DataWrappedResponse, ErrorResponse, ValidationErrorResponse } from '../exports/Types';
import BackendApiClient from './BackendApiClient';
import { PaymentIntent } from '@stripe/stripe-js';
import qs from 'qs';
import { CustomParamsSerializer } from 'axios';
import { OrderFulfillmentScope } from '../exports/Constants';
import { store } from '../../store';
import { OrdersApiSliceTags, ordersApiSlice } from '../../store/api-slices';
import InitialLoadingManager from './InitialLoadingManager';

class OrderManager {
  public getOrder = async (uuid: string): Promise<DataWrappedResponse<Order>> =>
    BackendApiClient.request({
      method: 'GET',
      url: `/orders/${uuid}`,
      params: InitialLoadingManager.getFranchiseParams(),
    });

  public getOrderHistory = async (
    params = { limit: 10 },
    status?: OrderFulfillmentScope,
    companyId?: number,
  ): Promise<DataWrappedResponse<Order[]>> =>
    BackendApiClient.request({
      method: 'GET',
      url: '/orders',
      params: {
        ...(companyId && { filter: { company_id: companyId } }),
        ...(status && { [status]: true }),
        ...params,
      },
      paramsSerializer: qs.stringify as unknown as CustomParamsSerializer,
    });

  public getOrderPaymentStatus = async (uuid: string): Promise<DataWrappedResponse<Partial<Order>>> =>
    BackendApiClient.request({
      method: 'GET',
      url: `/orders/${uuid}/payment-status`,
    });

  public createOrder = async (
    data: OrderData,
    errorHandlers?: ErrorHandlerInterface,
    headers?: IMap<string>,
  ): Promise<
    | DataWrappedResponse<Order, 201>
    | ErrorResponse<{ order: Order; payment_intent: PaymentIntent }, 402>
    | ValidationErrorResponse
  > => {
    const request = await BackendApiClient.request(
      {
        method: 'POST',
        url: '/orders',
        data,
        headers,
      },
      false,
      errorHandlers,
    );

    const { response, success } = request;

    if (success) {
      this.saveOrderRedux(response?.data?.data);
      store.dispatch(ordersApiSlice.util.invalidateTags([OrdersApiSliceTags.Order]));
    }

    return request;
  };

  public updateOrder = async (
    uuid: string,
    data: OrderData,
    errorHandlers?: ErrorHandlerInterface,
    headers?: IMap<string>,
  ): Promise<
    | DataWrappedResponse<Order, 201>
    | ErrorResponse<{ order: Order; payment_intent: PaymentIntent }, 402>
    | ValidationErrorResponse
  > => {
    const request = await BackendApiClient.request(
      {
        method: 'PUT',
        url: `/orders/${uuid}`,
        data,
        headers,
      },
      false,
      errorHandlers,
    );

    const { response, success } = request;

    if (success) {
      this.saveOrderRedux(response?.data?.data);
    }

    return request;
  };

  public async draftOrder(data: OrderData): Promise<DataWrappedResponse<Order, 200> | ValidationErrorResponse> {
    return BackendApiClient.request({
      method: 'POST',
      url: '/order-drafts',
      data,
    });
  }

  private saveOrderRedux(response: Order) {
    ReduxActions.dispatch(actionCreators.order.updateOrder(response));
  }
}

export default new OrderManager();
